import React, { useContext, useMemo, useEffect } from 'react'
import { Redirect } from 'react-router-dom'
import { CSVLink } from 'react-csv'
import { mdiCheck, mdiDownload } from '@mdi/js'
import { Button, Row, Col, Form } from 'react-bootstrap'
import { Helmet } from 'react-helmet'
import { Icon } from '@jcu/cookbook-react'
import { OrcidIdLink } from '../components'
import { useSearchHandler } from '../lib/search'
import { checkAccess } from '../lib/auth'
import { ApplicationContext } from './App'
import { useResultTable } from '../lib/resultTable'

const allowedRoles = ['viewer', 'admin']

const Search = () => {
  const { alertHandler, user, requester } = useContext(ApplicationContext)

  const { searchTerm, hasSearched, updateSearchTerm, performSearch, results } =
    useSearchHandler({ alertHandler, requester })

  const columns = useMemo(() => {
    const renderTokens = tokens => {
      const friendlyText = token => {
        // eslint-disable-next-line default-case
        switch (token) {
          case '/authenticate':
            return 'ORCID iD linked'
          case '/read-limited':
            return 'Read limited-access info'
          case '/person/update':
            return 'Add/update personal info'
          case '/activities/update':
            return 'Add/update activities'
        }
      }
      return (
        <ul className="list-unstyled small">
          {tokens.map((token, index) => (
            <li key={index}>
              <Icon
                style={{ verticalAlign: 'middle' }}
                path={mdiCheck}
                color="008a00"
                size="1.5rem"
              />{' '}
              {friendlyText(token)}
            </li>
          ))}
        </ul>
      )
    }

    return [
      {
        Header: 'Username',
        accessor: 'username',
        Cell: ({ value }) => {
          return <code>{value}</code>
        },
      },
      {
        Header: 'Personal Name',
        accessor: 'personal_name',
      },
      {
        Header: 'Family Name',
        accessor: 'family_name',
      },
      {
        Header: 'Email',
        accessor: 'email',
        Cell: ({ value }) => <a href={`mailto:${value}`}>{value}</a>,
      },
      {
        Header: 'ORCID iD',
        accessor: 'orcid_id',
        Cell: ({ value, row }) =>
          !!value ? (
            <OrcidIdLink
              orcidId={value}
              sandbox={row.values.sandbox}
              format="compact"
            />
          ) : null,
      },
      { accessor: 'sandbox' },
      {
        Header: 'Access Allowed',
        accessor: 'tokens',
        Cell: ({ value }) => renderTokens(value),
        sortType: (rowA, rowB, columnId) => {
          if (rowA.original[columnId].length > rowB.original[columnId].length)
            return -1
          if (rowB.original[columnId].length > rowA.original[columnId].length)
            return 1
          return 0
        },
      },
    ]
  }, [])

  const { render, updateData } = useResultTable({
    initialData: results,
    columns,
    initialState: {
      hiddenColumns: ['sandbox'],
    },
  })

  useEffect(() => {
    updateData(results)
  }, [results, updateData])

  const handleSearch = async event => {
    event.preventDefault()
    performSearch('/orcid/search')
  }

  const handleSearchChange = event => {
    updateSearchTerm(event.target.value)
  }

  if (!checkAccess(user, allowedRoles)) {
    return <Redirect to="/" />
  }

  const hasResults = results && results.length > 0
  let resultsListing
  if (hasResults) {
    resultsListing = render()
  } else if (hasSearched) {
    resultsListing = (
      <p>
        No results found. Try another search term or by being more specific.
      </p>
    )
  }

  return (
    <>
      <Helmet>
        <title>Search</title>
      </Helmet>
      <Row>
        <Col>
          <h1>Search ORCID iD Links</h1>
          <p className="lead mb-3">
            Search by name, email, login ID or organisational unit to find ORCID
            iDs. You can also search by ORCID iD to find a JCU person.
          </p>

          <Form onSubmit={handleSearch}>
            <Row xs={12} sm="auto" className="mb-2 gy-1 gx-1">
              <Col md={4} className="mb-1">
                <Form.Control
                  type="text"
                  placeholder="Enter search term"
                  onChange={handleSearchChange}
                />
                {hasResults && (
                  <div className="my-1">
                    <Form.Text>
                      {results.length} result{results.length !== 1 && 's'} found
                    </Form.Text>
                  </div>
                )}
              </Col>
              <Col md={2} className="me-sm-auto">
                <div className="d-grid">
                  <Button variant="primary" type="submit">
                    Search
                  </Button>
                </div>
              </Col>

              {hasResults && (
                <Col md="auto">
                  <div className="d-grid">
                    <Button
                      variant="secondary"
                      as={CSVLink}
                      data={results}
                      filename={`jcu-orcid-${
                        new Date().toISOString().split('T')[0]
                      }-${searchTerm}.csv`}
                    >
                      <Icon
                        style={{ verticalAlign: 'middle' }}
                        path={mdiDownload}
                        size="1.5rem"
                      />{' '}
                      Download as CSV
                    </Button>
                  </div>
                </Col>
              )}
            </Row>
          </Form>

          {resultsListing}
        </Col>
      </Row>
    </>
  )
}

export default Search
export { allowedRoles }
